import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography
} from '@mui/material';
import {
  Assessment,
  CheckCircle,
  Error as ErrorIcon,
  FileDownload,
  OpenInNew,
  ReportProblem
} from '@mui/icons-material';
import { apiService } from '@/services/api';
import { ClientCell } from '@/components/ClientCell';
import { CopyableId } from '@/components/CopyableId';
import { ProductCell } from '@/components/ProductCell';
import { useRefresh } from '@/contexts/RefreshContext';
import { useFilters } from '@/contexts/FilterContext';
import { useProductsMap } from '@/hooks/useProductsMap';
import { ProductScopeGate } from '@/components/ProductScopeGate';

type Outcome = 'authorized' | 'rejected' | 'error';

type AuditSummary = {
  total: number;
  authorized: number;
  rejected: number;
  error: number;
  successRate: number;
};

type AuditEvent = {
  id: string;
  attemptId: string;
  tenantId: string;
  invoiceId: string;
  paymentId: string;
  invoiceExternalId?: string;
  billingPeriod?: string;
  amountCents?: number;
  customerName?: string;
  customerCnpjCpf?: string;
  emissionDateYmd?: string;
  rpsNumero?: number;
  envioTeste?: boolean;
  outcome?: Outcome;
  nfseExternalId?: string;
  nfseNumero?: string;
  rejectionReason?: string;
  errorMessage?: string;
  ipmHttpStatus?: number;
  startedAt?: string;
  finishedAt?: string;
};

type TimeseriesRow = {
  day: string;
  total: number;
  authorized: number;
  rejected: number;
  error: number;
};

type ReasonRow = {
  reason: string;
  outcome: 'rejected' | 'error';
  count: number;
  lastSeenAt?: string;
};

type TenantRow = {
  tenantId: string;
  total: number;
  authorized: number;
  rejected: number;
  error: number;
  successRate: number;
  lastSeenAt?: string;
};

const fmtMoney = (cents?: number) => {
  const v = Number.isFinite(Number(cents)) ? Number(cents) / 100 : 0;
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);
};

const fmtPct = (v: number) => `${(Math.max(0, Math.min(1, v)) * 100).toFixed(2)}%`;

function downloadTextFile(name: string, content: string, mime = 'text/plain;charset=utf-8') {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

function csvEscape(v: unknown): string {
  const s = String(v ?? '');
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function OutcomeChip({ outcome }: { outcome: Outcome | undefined }) {
  if (outcome === 'authorized') return <Chip size="small" color="success" label="Autorizada" />;
  if (outcome === 'rejected') return <Chip size="small" color="warning" label="Rejeitada" />;
  if (outcome === 'error') return <Chip size="small" color="error" label="Erro" />;
  return <Chip size="small" variant="outlined" label="—" />;
}

export const NfseObservabilityPage: React.FC = () => {
  const { setRefreshCallback, isRefreshing } = useRefresh();
  const { selectedClients, productId, selectedProductIds } = useFilters();
  const productsById = useProductsMap();

  const today = new Date();
  const [from, setFrom] = useState(
    new Date(today.getFullYear(), today.getMonth(), 1).toISOString().slice(0, 10)
  );
  const [to, setTo] = useState(today.toISOString().slice(0, 10));
  const [outcome, setOutcome] = useState<Outcome | ''>('');
  const [invoiceId, setInvoiceId] = useState('');
  const [paymentId, setPaymentId] = useState('');

  const tenantId = useMemo(() => {
    if (selectedClients.length === 1) return selectedClients[0];
    return '';
  }, [selectedClients]);

  const effectiveProductId = useMemo(() => {
    const fromSingle = productId?.trim();
    if (fromSingle) return fromSingle;
    if (selectedProductIds.length === 1) return selectedProductIds[0];
    return '';
  }, [productId, selectedProductIds]);

  const [summary, setSummary] = useState<AuditSummary | null>(null);
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [timeseries, setTimeseries] = useState<TimeseriesRow[]>([]);
  const [reasons, setReasons] = useState<ReasonRow[]>([]);
  const [tenants, setTenants] = useState<TenantRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [tab, setTab] = useState<'overview' | 'events' | 'reasons' | 'tenants'>('overview');
  const [exporting, setExporting] = useState(false);
  const exportAbortRef = useRef<{ aborted: boolean }>({ aborted: false });

  const load = useCallback(
    async (mode: 'reset' | 'more' = 'reset') => {
      try {
        setLoading(true);
        setLoadError(null);

        const commonParams = {
          from,
          to,
          ...(effectiveProductId ? { productId: effectiveProductId } : {}),
          ...(tenantId ? { tenantId } : {})
        };

        const [sumRes, tsRes, reasonsRes, tenantsRes, listRes] = await Promise.all([
          apiService.getBillingNfseAuditSummary(commonParams),
          apiService.getBillingNfseAuditTimeseries(commonParams),
          apiService.getBillingNfseAuditReasons({ ...commonParams, limit: 25 }),
          apiService.listBillingNfseAuditTenants({ ...commonParams, limit: 80 }),
          apiService.listBillingNfseAuditEvents({
            ...commonParams,
            ...(outcome ? { outcome } : {}),
            ...(invoiceId.trim() ? { invoiceId: invoiceId.trim() } : {}),
            ...(paymentId.trim() ? { paymentId: paymentId.trim() } : {}),
            limit: 50,
            ...(mode === 'more' && nextCursor ? { starting_after: nextCursor } : {})
          })
        ]);

        setSummary(sumRes.data as AuditSummary);
        setTimeseries(
          Array.isArray((tsRes.data as any)?.data)
            ? ((tsRes.data as any).data as TimeseriesRow[])
            : []
        );
        setReasons(
          Array.isArray((reasonsRes.data as any)?.data)
            ? ((reasonsRes.data as any).data as ReasonRow[])
            : []
        );
        setTenants(
          Array.isArray((tenantsRes.data as any)?.data)
            ? ((tenantsRes.data as any).data as TenantRow[])
            : []
        );

        const data = Array.isArray((listRes.data as any)?.data)
          ? ((listRes.data as any).data as AuditEvent[])
          : [];
        const hasMore = Boolean((listRes.data as any)?.has_more);
        const cursor = (listRes.data as any)?.next_cursor ?? null;

        setEvents(prev => (mode === 'more' ? [...prev, ...data] : data));
        setNextCursor(hasMore ? (cursor as string) : null);
      } catch (err: unknown) {
        setLoadError(err);
        setSummary(null);
        setEvents([]);
        setTimeseries([]);
        setReasons([]);
        setTenants([]);
        setNextCursor(null);
      } finally {
        setLoading(false);
      }
    },
    [from, to, tenantId, outcome, invoiceId, paymentId, nextCursor, effectiveProductId]
  );

  useEffect(() => {
    load('reset');
  }, [load]);

  useEffect(() => {
    setRefreshCallback(() => load('reset'));
    return () => setRefreshCallback(null);
  }, [load, setRefreshCallback]);

  const selectionHint =
    selectedClients.length > 1
      ? 'Filtro de clientes: múltiplos selecionados. Nesta tela, o filtro por tenantId é aplicado somente quando 1 cliente está selecionado.'
      : selectedClients.length === 0
        ? 'Dica: selecione um cliente no filtro global (topo) para auditar um tenant específico.'
        : null;

  const maxTs = useMemo(
    () => Math.max(1, ...timeseries.map(r => Number(r.total) || 0)),
    [timeseries]
  );

  const handleExportCsv = useCallback(async () => {
    try {
      setExporting(true);
      exportAbortRef.current.aborted = false;

      const commonParams = {
        from,
        to,
        ...(effectiveProductId ? { productId: effectiveProductId } : {}),
        ...(tenantId ? { tenantId } : {})
      };

      const all: AuditEvent[] = [];
      let cursor: string | null | undefined = undefined;
      let hasMore = true;
      const hardLimit = 5000; // proteção anti-freeze

      while (hasMore && all.length < hardLimit && !exportAbortRef.current.aborted) {
        const res: any = await apiService.listBillingNfseAuditEvents({
          ...commonParams,
          ...(outcome ? { outcome } : {}),
          ...(invoiceId.trim() ? { invoiceId: invoiceId.trim() } : {}),
          ...(paymentId.trim() ? { paymentId: paymentId.trim() } : {}),
          limit: 200,
          ...(cursor ? { starting_after: cursor } : {})
        });
        const data = Array.isArray((res.data as any)?.data)
          ? ((res.data as any).data as AuditEvent[])
          : [];
        all.push(...data);
        hasMore = Boolean((res.data as any)?.has_more);
        cursor = (res.data as any)?.next_cursor ?? null;
        if (!data.length) break;
      }

      const header = [
        'attemptId',
        'tenantId',
        'invoiceId',
        'invoiceExternalId',
        'billingPeriod',
        'paymentId',
        'amountCents',
        'customerName',
        'customerCnpjCpf',
        'emissionDateYmd',
        'rpsNumero',
        'envioTeste',
        'outcome',
        'nfseExternalId',
        'nfseNumero',
        'ipmHttpStatus',
        'rejectionReason',
        'errorMessage',
        'startedAt',
        'finishedAt'
      ];
      const lines = [header.join(',')];
      for (const e of all) {
        const row = [
          e.attemptId,
          e.tenantId,
          e.invoiceId,
          e.invoiceExternalId || '',
          e.billingPeriod || '',
          e.paymentId,
          e.amountCents ?? '',
          e.customerName || '',
          e.customerCnpjCpf || '',
          e.emissionDateYmd || '',
          e.rpsNumero ?? '',
          e.envioTeste ? 'true' : 'false',
          e.outcome || '',
          e.nfseExternalId || '',
          e.nfseNumero || '',
          e.ipmHttpStatus ?? '',
          e.rejectionReason || '',
          e.errorMessage || '',
          e.startedAt || '',
          e.finishedAt || ''
        ].map(csvEscape);
        lines.push(row.join(','));
      }

      const name = `nfse-audit_${from}_${to}${tenantId ? `_tenant_${tenantId}` : ''}.csv`;
      downloadTextFile(name, lines.join('\n'), 'text/csv;charset=utf-8');
    } catch (e: any) {
      setExportError(
        (e as { response?: { data?: { message?: string } }; message?: string }).response?.data
          ?.message ||
          (e as Error).message ||
          'Falha ao exportar CSV'
      );
    } finally {
      setExporting(false);
    }
  }, [from, to, tenantId, outcome, invoiceId, paymentId, effectiveProductId]);

  const scopedProductId = effectiveProductId || undefined;

  return (
    <ProductScopeGate
      productId={scopedProductId}
      requireSelection={false}
      integration={scopedProductId ? 'billing' : 'core'}
      loading={loading}
      loadError={loadError}
      isEmpty={false}
      onRetry={() => load('reset')}
    >
      <Box sx={{ p: 3 }}>
        <Card variant="outlined" sx={{ mb: 3, bgcolor: 'action.hover' }}>
          <CardContent>
            <Stack spacing={1.5}>
              <Typography variant="h5">Observabilidade NFS-e</Typography>
              <Typography variant="body2" color="text.secondary">
                Auditoria detalhada por cobrança: tentativas de emissão, rejeições, erros IPM,
                documentos autorizados e investigação por cliente.
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                <ProductCell
                  productId={productId}
                  name={productsById.get(productId)?.displayName}
                  avatarUrl={productsById.get(productId)?.avatarUrl}
                />
                <Chip size="small" variant="outlined" label="Fonte: nfseauditevents no billing" />
                <Chip
                  size="small"
                  variant="outlined"
                  label="Ambiente fiscal conforme config global"
                />
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        {selectionHint && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {selectionHint}
          </Alert>
        )}

        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item>
              <TextField
                label="De"
                type="date"
                value={from}
                onChange={e => setFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            </Grid>
            <Grid item>
              <TextField
                label="Até"
                type="date"
                value={to}
                onChange={e => setTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            </Grid>
            <Grid item sx={{ minWidth: 180 }}>
              <TextField
                label="Resultado"
                value={outcome}
                onChange={e => setOutcome(e.target.value as any)}
                size="small"
                select
                fullWidth
                InputLabelProps={{ shrink: true }}
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="authorized">Autorizada</MenuItem>
                <MenuItem value="rejected">Rejeitada</MenuItem>
                <MenuItem value="error">Erro</MenuItem>
              </TextField>
            </Grid>
            <Grid item sx={{ minWidth: 240 }}>
              <TextField
                label="invoiceId"
                value={invoiceId}
                onChange={e => setInvoiceId(e.target.value)}
                size="small"
                fullWidth
              />
            </Grid>
            <Grid item sx={{ minWidth: 240 }}>
              <TextField
                label="paymentId"
                value={paymentId}
                onChange={e => setPaymentId(e.target.value)}
                size="small"
                fullWidth
              />
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                onClick={() => load('reset')}
                disabled={loading || isRefreshing}
                startIcon={loading || isRefreshing ? <CircularProgress size={20} /> : null}
              >
                Atualizar
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant="outlined"
                onClick={handleExportCsv}
                disabled={exporting || loading}
                startIcon={exporting ? <CircularProgress size={18} /> : <FileDownload />}
              >
                Exportar CSV
              </Button>
            </Grid>
            {tenantId ? (
              <Grid item>
                <Chip
                  size="small"
                  variant="outlined"
                  label={
                    <Box component="span">
                      tenantId: <CopyableId value={tenantId} label="tenantId" />
                    </Box>
                  }
                />
              </Grid>
            ) : null}
          </Grid>
        </Paper>

        {exportError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setExportError(null)}>
            {exportError}
          </Alert>
        )}

        <Paper sx={{ mb: 3 }}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ px: 2, pt: 1 }}
          >
            <Tab value="overview" label="Visão geral" />
            <Tab value="events" label="Eventos" />
            <Tab value="reasons" label="Motivos" />
            <Tab value="tenants" label="Tenants" />
          </Tabs>
        </Paper>

        {!summary && loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : summary && tab === 'overview' ? (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Assessment color="primary" />
                    <Typography variant="overline">Total</Typography>
                  </Box>
                  <Typography variant="h5">{summary.total}</Typography>
                  <Typography variant="caption">
                    Taxa de sucesso: {fmtPct(summary.successRate)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <CheckCircle color="success" />
                    <Typography variant="overline">Autorizadas</Typography>
                  </Box>
                  <Typography variant="h5">{summary.authorized}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <ReportProblem color="warning" />
                    <Typography variant="overline">Rejeitadas</Typography>
                  </Box>
                  <Typography variant="h5">{summary.rejected}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <ErrorIcon color="error" />
                    <Typography variant="overline">Erros</Typography>
                  </Box>
                  <Typography variant="h5">{summary.error}</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        ) : null}

        {tab === 'overview' ? (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={7}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Tendência diária (UTC)
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Volume total por dia (barra) e distribuição por status.
                </Typography>
                {timeseries.length === 0 ? (
                  <Alert severity="info">Sem dados no período.</Alert>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {timeseries.map(r => (
                      <Box key={r.day} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 92 }}>
                          <Typography variant="caption" color="text.secondary">
                            {r.day}
                          </Typography>
                        </Box>
                        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            sx={{
                              height: 10,
                              width: `${Math.max(2, Math.round((100 * (r.total || 0)) / maxTs))}%`,
                              backgroundColor: '#64748b',
                              borderRadius: 1
                            }}
                          />
                          <Typography variant="caption" color="text.secondary">
                            total {r.total} | ok {r.authorized} | rej {r.rejected} | err {r.error}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}
              </Paper>
            </Grid>
            <Grid item xs={12} md={5}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Top motivos (prévia)
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Agrupado por mensagem (primeiros 240 caracteres).
                </Typography>
                {reasons.length === 0 ? (
                  <Alert severity="info">Sem motivos no período.</Alert>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {reasons.slice(0, 10).map((r, idx) => (
                      <Box
                        key={`${idx}-${r.reason}`}
                        sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}
                      >
                        <Chip
                          size="small"
                          color={r.outcome === 'rejected' ? 'warning' : 'error'}
                          label={r.outcome === 'rejected' ? 'rejeição' : 'erro'}
                        />
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {r.count}x
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {r.reason}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>
        ) : null}

        {tab === 'events' ? (
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Eventos (drill-down)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Cada linha representa uma tentativa de emissão para uma cobrança (invoice/payment) e o
              resultado final.
            </Typography>

            <Divider sx={{ mb: 2 }} />

            {events.length === 0 && !loading ? (
              <Alert severity="info">Nenhum evento encontrado para os filtros atuais.</Alert>
            ) : null}

            {events.map(ev => (
              <Box key={ev.id} sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <OutcomeChip outcome={ev.outcome} />
                  <Chip
                    size="small"
                    variant="outlined"
                    label={ev.billingPeriod ? `Ref: ${ev.billingPeriod}` : 'Ref: —'}
                  />
                  <Chip
                    size="small"
                    variant="outlined"
                    label={
                      ev.invoiceExternalId ? (
                        `Invoice: ${ev.invoiceExternalId}`
                      ) : (
                        <Box component="span">
                          invoiceId: <CopyableId value={ev.invoiceId} label="invoiceId" />
                        </Box>
                      )
                    }
                  />
                  <ClientCell name={ev.customerName} tenantId={ev.tenantId} size="small" />
                  <Chip size="small" variant="outlined" label={fmtMoney(ev.amountCents)} />
                  {ev.envioTeste ? (
                    <Chip size="small" color="info" label="Homologação" />
                  ) : (
                    <Chip size="small" label="Produção" variant="outlined" />
                  )}
                  {ev.nfseExternalId ? (
                    <Chip size="small" color="success" label={`NFSe: ${ev.nfseExternalId}`} />
                  ) : null}
                  {ev.nfseNumero ? (
                    <Chip size="small" variant="outlined" label={`Número: ${ev.nfseNumero}`} />
                  ) : null}
                  {ev.nfseExternalId ? (
                    <>
                      <Button
                        size="small"
                        variant="text"
                        startIcon={<OpenInNew />}
                        onClick={() =>
                          window.open(
                            `/billing/nfse/${encodeURIComponent(ev.nfseExternalId!)}/pdf`,
                            '_blank',
                            'noopener,noreferrer'
                          )
                        }
                      >
                        PDF
                      </Button>
                      <Button
                        size="small"
                        variant="text"
                        startIcon={<OpenInNew />}
                        onClick={() =>
                          window.open(
                            `/billing/nfse/${encodeURIComponent(ev.nfseExternalId!)}/xml`,
                            '_blank',
                            'noopener,noreferrer'
                          )
                        }
                      >
                        XML
                      </Button>
                    </>
                  ) : null}
                </Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mt: 0.5 }}
                >
                  Tentativa: <CopyableId value={ev.attemptId} label="attemptId" /> • paymentId:{' '}
                  <CopyableId value={ev.paymentId} label="paymentId" /> • Início:{' '}
                  {ev.startedAt || '—'} • Fim: {ev.finishedAt || '—'} • RPS: {ev.rpsNumero ?? '—'} •
                  HTTP: {ev.ipmHttpStatus ?? '—'}
                </Typography>
                {(ev.rejectionReason || ev.errorMessage) && (
                  <Alert severity={ev.outcome === 'rejected' ? 'warning' : 'error'} sx={{ mt: 1 }}>
                    {ev.rejectionReason || ev.errorMessage}
                  </Alert>
                )}
                <Divider sx={{ mt: 2 }} />
              </Box>
            ))}

            <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1 }}>
              <Button
                variant="outlined"
                disabled={!nextCursor || loading}
                onClick={() => load('more')}
              >
                {nextCursor ? 'Carregar mais' : 'Sem mais resultados'}
              </Button>
            </Box>
          </Paper>
        ) : null}

        {tab === 'reasons' ? (
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Motivos (rejeição/erro)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Use isso para atacar causas-raiz e acompanhar se a plataforma está “saudável” em
              produção.
            </Typography>
            <Divider sx={{ mb: 2 }} />
            {reasons.length === 0 ? (
              <Alert severity="info">Sem motivos no período.</Alert>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {reasons.map((r, idx) => (
                  <Box
                    key={`${idx}-${r.reason}`}
                    sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}
                  >
                    <Chip
                      size="small"
                      color={r.outcome === 'rejected' ? 'warning' : 'error'}
                      label={r.outcome === 'rejected' ? 'rejeição' : 'erro'}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {r.count}x
                      </Typography>
                      <Typography variant="body2">{r.reason}</Typography>
                      {r.lastSeenAt ? (
                        <Typography variant="caption" color="text.secondary">
                          Última ocorrência: {r.lastSeenAt}
                        </Typography>
                      ) : null}
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Paper>
        ) : null}

        {tab === 'tenants' ? (
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Tenants (ranking por volume)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Útil para identificar tenants com problemas recorrentes (cadastro fiscal/endereço,
              CNPJ/CPF, etc.).
            </Typography>
            <Divider sx={{ mb: 2 }} />
            {tenants.length === 0 ? (
              <Alert severity="info">Sem dados no período.</Alert>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {tenants.map(t => (
                  <Box
                    key={t.tenantId}
                    sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}
                  >
                    <ClientCell tenantId={t.tenantId} size="small" />
                    <Chip size="small" label={`total ${t.total}`} />
                    <Chip size="small" color="success" label={`ok ${t.authorized}`} />
                    <Chip size="small" color="warning" label={`rej ${t.rejected}`} />
                    <Chip size="small" color="error" label={`err ${t.error}`} />
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`sucesso ${fmtPct(t.successRate)}`}
                    />
                  </Box>
                ))}
              </Box>
            )}
          </Paper>
        ) : null}
      </Box>
    </ProductScopeGate>
  );
};

export default NfseObservabilityPage;
